import React, { useState, useEffect } from "react";
import { X, Users, UserPlus, UserMinus, Shield, Eye, Edit } from "lucide-react";
import useCalendar from "../../hooks/useCalendar";
import AlertModal from "./AlertModal";

/**
 * 캘린더 멤버 관리 모달
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {function} onClose - 닫기 핸들러
 * @param {object} calendar - 캘린더 정보
 */
export default function CalendarMembersModal({ isOpen, onClose, calendar }) {
  const {
    fetchCalendarMembers,
    inviteToCalendar,
    banFromCalendar,
    mandatePermission,
  } = useCalendar();
  const [members, setMembers] = useState([]);
  const [joinableMembers, setJoinableMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [selectedInviteUsers, setSelectedInviteUsers] = useState([]);
  const [invitePermission, setInvitePermission] = useState("view");
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen && calendar) {
      loadMembers();
    }
  }, [isOpen, calendar]);

  const loadMembers = async () => {
    setLoading(true);
    const result = await fetchCalendarMembers(calendar.id);
    if (result.ok) {
      setMembers(result.members);
      setJoinableMembers(result.joinableMembers);
    } else if (result.permissionDenied) {
      setAlertConfig({
        title: "권한 없음",
        message: "멤버 목록을 볼 권한이 없습니다.",
        type: "error",
      });
      setShowAlert(true);
    }
    setLoading(false);
  };

  const handleInvite = async () => {
    if (selectedInviteUsers.length === 0) return;

    const permissions = selectedInviteUsers.map((userId) => ({
      user_id: userId,
      permission: invitePermission,
    }));

    const result = await inviteToCalendar(calendar.id, permissions);

    if (result.ok) {
      setShowInviteForm(false);
      setSelectedInviteUsers([]);
      setInvitePermission("view");
      setSearchQuery("");
      loadMembers();
      setAlertConfig({
        title: "성공",
        message: `${selectedInviteUsers.length}명의 멤버를 초대했습니다.`,
        type: "success",
      });
      setShowAlert(true);
    } else if (result.isPublic) {
      setAlertConfig({
        title: "공개 캘린더",
        message: "공개 캘린더는 별도로 초대할 필요가 없습니다.",
        type: "info",
      });
      setShowAlert(true);
    } else if (result.isPersonal) {
      setAlertConfig({
        title: "개인 캘린더",
        message: "개인 캘린더는 공유할 수 없습니다.",
        type: "warning",
      });
      setShowAlert(true);
    } else if (result.permissionDenied) {
      setAlertConfig({
        title: "권한 없음",
        message: "멤버를 초대할 권한이 없습니다.",
        type: "error",
      });
      setShowAlert(true);
    }
  };

  const handleBan = async (userId) => {
    setAlertConfig({
      title: "멤버 추방",
      message: "정말로 이 멤버를 추방하시겠습니까?",
      type: "warning",
      onConfirm: async () => {
        const result = await banFromCalendar(calendar.id, [userId]);
        if (result.ok) {
          loadMembers();
          setAlertConfig({
            title: "성공",
            message: "멤버를 추방했습니다.",
            type: "success",
          });
          setShowAlert(true);
        } else if (result.permissionDenied) {
          setAlertConfig({
            title: "권한 없음",
            message: "멤버를 추방할 권한이 없습니다.",
            type: "error",
          });
          setShowAlert(true);
        }
      },
    });
    setShowAlert(true);
  };

  const handleMandate = async (userId) => {
    setAlertConfig({
      title: "권한 위임",
      message: "정말로 이 멤버에게 방장 권한을 위임하시겠습니까?",
      type: "warning",
      onConfirm: async () => {
        const result = await mandatePermission(userId, calendar.id);
        if (result.ok) {
          onClose();
          setAlertConfig({
            title: "성공",
            message: "권한을 위임했습니다.",
            type: "success",
          });
          setShowAlert(true);
        } else if (result.isPersonal) {
          setAlertConfig({
            title: "개인 캘린더",
            message: "개인 캘린더는 권한을 위임할 수 없습니다.",
            type: "warning",
          });
          setShowAlert(true);
        } else if (result.permissionDenied) {
          setAlertConfig({
            title: "권한 없음",
            message: "권한을 위임할 수 없습니다.",
            type: "error",
          });
          setShowAlert(true);
        }
      },
    });
    setShowAlert(true);
  };

  const getPermissionIcon = (permission) => {
    return permission === "edit" ? (
      <Edit className="w-4 h-4 text-blue-500" />
    ) : (
      <Eye className="w-4 h-4 text-gray-500" />
    );
  };

  const getPermissionLabel = (permission) => {
    return permission === "edit" ? "편집" : "보기";
  };

  if (!isOpen || !calendar) return null;

  const filteredJoinableMembers = joinableMembers.filter((member) =>
    member.user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCurrentUser = () => {
    return members.find((m) => m.is_me);
  };

  const currentUser = getCurrentUser();
  const isOwner = currentUser && calendar.owner_id === currentUser.user_id;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
          {/* 헤더 */}
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="w-6 h-6 text-blue-500 mr-2" />
                <div>
                  <h3 className="text-lg font-semibold">
                    {calendar.calendar_name} 멤버 관리
                  </h3>
                  <p className="text-sm text-gray-500">
                    {members.length}명의 멤버가 참여 중
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 콘텐츠 */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="text-center text-gray-500">로딩 중...</div>
            ) : (
              <div>
                {/* 초대 버튼 */}
                {isOwner && calendar.calendar_type === "shared" && (
                  <div className="mb-6">
                    {!showInviteForm ? (
                      <button
                        onClick={() => setShowInviteForm(true)}
                        className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      >
                        <UserPlus className="w-5 h-5 mr-2" />
                        멤버 초대
                      </button>
                    ) : (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-3">초대할 멤버 선택</h4>

                        {/* 검색 */}
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="이름으로 검색..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />

                        {/* 권한 선택 */}
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            권한 설정
                          </label>
                          <select
                            value={invitePermission}
                            onChange={(e) =>
                              setInvitePermission(e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="view">보기</option>
                            <option value="edit">편집</option>
                          </select>
                        </div>

                        {/* 초대 가능한 멤버 목록 */}
                        <div className="max-h-48 overflow-y-auto border rounded-lg mb-3">
                          {filteredJoinableMembers.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                              초대 가능한 멤버가 없습니다.
                            </div>
                          ) : (
                            filteredJoinableMembers.map((member) => (
                              <label
                                key={member.user_id}
                                className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedInviteUsers.includes(
                                    member.user_id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedInviteUsers([
                                        ...selectedInviteUsers,
                                        member.user_id,
                                      ]);
                                    } else {
                                      setSelectedInviteUsers(
                                        selectedInviteUsers.filter(
                                          (id) => id !== member.user_id
                                        )
                                      );
                                    }
                                  }}
                                  className="mr-3"
                                />
                                <span>{member.user_name}</span>
                              </label>
                            ))
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={handleInvite}
                            disabled={selectedInviteUsers.length === 0}
                            className={`px-4 py-2 rounded-lg ${
                              selectedInviteUsers.length === 0
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-500 text-white hover:bg-blue-600"
                            }`}
                          >
                            {selectedInviteUsers.length > 0
                              ? `${selectedInviteUsers.length}명 초대`
                              : "초대"}
                          </button>
                          <button
                            onClick={() => {
                              setShowInviteForm(false);
                              setSelectedInviteUsers([]);
                              setInvitePermission("view");
                              setSearchQuery("");
                            }}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 멤버 목록 */}
                <h4 className="font-medium text-sm text-gray-700 mb-3 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  현재 참여 중인 멤버 ({members.length}명)
                </h4>
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.user_id}
                      className={`flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm ${
                        member.is_me ? "bg-blue-50 border-blue-200" : ""
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-gray-600">
                            {member.user_name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium">
                            {member.user_name}
                            {member.is_me && (
                              <span className="text-sm text-blue-600 ml-2">
                                (나)
                              </span>
                            )}
                            {member.user_id === calendar.owner_id && (
                              <Shield
                                className="inline w-4 h-4 text-yellow-500 ml-2"
                                title="방장"
                              />
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            {getPermissionIcon(member.permission)}
                            <span className="ml-1">
                              {getPermissionLabel(member.permission)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* 액션 버튼 */}
                      {isOwner &&
                        !member.is_me &&
                        member.user_id !== calendar.owner_id && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleMandate(member.user_id)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg"
                              title="방장 권한 위임"
                            >
                              <Shield className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleBan(member.user_id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="추방"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                    </div>
                  ))}
                </div>

                {members.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    멤버가 없습니다.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 하단 */}
          <div className="p-6 border-t">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        {...alertConfig}
      />
    </>
  );
}
